/**
 * AI director — the enemy faction's brain. Organized as sub-managers
 * (private methods below): economy, build planner, research, military
 * production, defense, scouting, and waves.
 *
 * The director OWNS its own Economy (seeded from the faction's
 * starting_resources), Workforce, ResearchState + TechEffects, and
 * TrainingQueues. It sees the world only through the per-tick context —
 * it has no perfect map knowledge. Attacks are gated on the knowledge
 * model: until a scout (or any warband) actually sees a player building
 * within its data `vision` range, no waves launch.
 *
 * Render-free: issues orders on Combatants and places buildings through
 * AiCallbacks (implemented by main.ts, which owns all views). No
 * three.js imports, no Math.random — seeded RNG via makeRng.
 */
import type { GameDB } from "../data/gamedb";
import type { Heightfield } from "./pcg";
import type {
  BuildingDef,
  FactionDef,
  TechDef,
  UnitDef,
} from "../data/types";
import type { Combatant, BuildingTarget } from "./combat";
import { statNum } from "./combat";
import type { PlacedBuilding } from "./buildings";
import type { RoadNetwork } from "./roads";
import {
  bestTierForFaction,
  formationUpkeepPerSec,
  ORG_TECH_LINE,
  tickFormationUpkeep,
  tiersForFaction,
  tierTrainCost,
  trainScale,
  tierById,
  type OrgTier,
} from "./organization";
import {
  costOf,
  footprintOf,
  missingRequirements,
  overlaps,
} from "./buildings";
import { Economy } from "./economy";
import type { ResourceNode } from "./resources";
import { makeRng } from "./resources";
import {
  canResearch,
  createResearch,
  startResearch,
  TechEffects,
  tickResearch,
  type ResearchState,
} from "./research";
import { trainableUnits, TrainingQueues, unitCost } from "./training";
import {
  commandProvided,
  commandStatus,
  MILITARY_ROLES,
  queuedMilitaryCount,
} from "./command";
import {
  farmHands,
  farmResource,
  Workforce,
  workforceUpkeep,
  type Worker,
  type WorkforceContext,
} from "./workers";
import {
  DIFFICULTIES,
  DIFFICULTY_IDS,
  type DifficultyConfig,
  type DifficultyId,
} from "./difficulty";
import { SpatialHash } from "./spatial";

/**
 * Scale rule: the director thinks in groups (warbands = formations), never
 * in soldiers and never with pairwise group scans. Target acquisition,
 * aggro, and discovery all go through a per-tick spatial hash, so AI cost
 * grows with the number of groups — not the number of soldiers — and
 * difficulty levels may scale unit counts freely.
 * Expensive sub-managers (defense, scouting, waves, planning) also run on
 * staggered sim-time intervals instead of every tick.
 */
const FIGHTER_CELL = 32;
/** Stagger intervals (sim seconds) for the heavier sub-managers. */
const DEFENSE_EVERY = 0.25;
const SCOUT_EVERY = 0.5;
const WAVE_EVERY = 0.5;
const PLAN_EVERY = 1.0;
/**
 * PROTOTYPE TUNING: seed force — formations the AI may muster from its
 * starting stockpile before any farm income exists, at the cheapest
 * unlocked tier. Once standing, the projected food governor takes over.
 */
const PROTOTYPE_SEED_FORCE = 2;
/**
 * PROTOTYPE TUNING: projected-upkeep headroom. The AI commits to a new
 * formation only when standing burn + the queued tier's upkeep fits
 * inside measured farm income × this factor — the queued tier's upkeep
 * is projected BEFORE training, never sized on the current average.
 */
const PROTOTYPE_FOOD_HEADROOM = 1.0;

/** Render-side services the director needs, implemented by main.ts. */
export interface AiCallbacks {
  spawnMilitary(
    unit: UnitDef,
    x: number,
    z: number,
    label: string,
    tier: OrgTier,
  ): Combatant;
  spawnWorker(unit: UnitDef, x: number, z: number): Worker;
  placeBuilding(
    def: BuildingDef,
    x: number,
    z: number,
    instant: boolean,
    team: 0 | 1,
  ): PlacedBuilding;
  /** Render-free: main.ts routes this to orderFormationMove. */
  orderMove(c: Combatant, x: number, z: number): void;
  notify(msg: string): void;
}

export interface AiTickContext {
  /** Every combatant on the field (both teams). */
  fighters: Combatant[];
  /** Every building on the field (both teams). */
  buildings: PlacedBuilding[];
  /** Player buildings, as attackable targets. */
  playerTargets: BuildingTarget[];
}

/** The AI's knowledge model: what it has actually observed. */
export interface AiKnowledge {
  playerBase: { x: number; z: number; seenAt: number } | null;
}

export interface AiDebugState {
  difficulty: DifficultyId;
  simNow: number;
  playerBaseKnown: boolean;
  scoutsActive: number;
  wavesLaunched: number;
  assaultsLaunched: number;
  warbands: number;
  workers: number;
  aiStocks: Record<string, number>;
  buildingsOwned: number;
  researchCompleted: string[];
  /** Current AI muster tier (best org tier unlocked by its drills). */
  musterTier: string;
  /** Tier id the muster governor chose this tick (null = hold). */
  musterChoice: string | null;
  /** Command pool: formations (+queued) used vs capacity. */
  commandUsed: number;
  commandTotal: number;
}

/** Scouting preference: cavalry first, then skirmishers. */
function scoutRank(unit: UnitDef): number {
  const role = unit.extra["role"];
  if (role === "cavalry") return 0;
  if (role === "skirmisher") return 1;
  return 2;
}

function roleOf(unit: UnitDef): string {
  return typeof unit.extra["role"] === "string" ? unit.extra["role"] : "";
}

function num(v: unknown, fb: number): number {
  return typeof v === "number" ? v : fb;
}

function costSum(cost: Record<string, number>): number {
  return Object.values(cost).reduce((a, b) => a + b, 0);
}

function dist2D(
  a: { x: number; z: number },
  b: { x: number; z: number },
): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

/** Pick the most combat-capable unit this faction can train (data-driven). */
/**
 * AI tech effects: the standard aggregation, with gather/haul rates
 * additionally scaled by the difficulty's economy multiplier.
 */
class AiTechEffects extends TechEffects {
  constructor(
    state: ResearchState,
    db: GameDB,
    private readonly config: DifficultyConfig,
  ) {
    super(state, db);
  }

  override gatherMultiplier(resource: string): number {
    return super.gatherMultiplier(resource) * this.config.gatherRateMult;
  }

  override haulMultiplier(): number {
    return super.haulMultiplier() * this.config.gatherRateMult;
  }
}

/** Research desirability: economy techs first, then military support. */
function scoreResearch(tech: TechDef): number {
  let s = 0;
  for (const e of tech.effects) {
    switch (e.type) {
      case "gather_rate":
        s += 3;
        break;
      case "income":
        s += 2;
        break;
      case "haul_speed":
      case "carry_capacity":
        s += 1.5;
        break;
      case "unit_stat":
      case "train_cost":
        s += 1;
        break;
      default:
        s += 0.1;
        break;
    }
  }
  return s;
}

interface PlanItem {
  def: BuildingDef;
  want: number;
}

interface Waypoint {
  x: number;
  z: number;
  at: number;
}

export class AiDirector {
  readonly workforce = new Workforce();
  readonly economy: Economy;
  readonly queues = new TrainingQueues();
  readonly research: ResearchState = createResearch();

  private readonly config: DifficultyConfig;
  private readonly rng: () => number;
  private readonly fx: AiTechEffects;
  private readonly knowledge: AiKnowledge = { playerBase: null };
  /** Command-building ids across all playable factions (data-driven). */
  private readonly commandIds: Set<string>;
  private readonly commandBuildingId: string;
  /**
   * Data-derived upper bound for unit vision queries, so the discovery
   * spatial query can never miss a faction-specific high-vision unit.
   * The exact per-unit range is still checked per candidate; this only
   * sizes the spatial query.
   */
  private readonly maxUnitVision: number;

  private plan: PlanItem[] | null = null;
  private readonly scouts = new Map<Combatant, Waypoint>();
  private readonly waveMembers = new Map<Combatant, number>();
  /** Shared node array, bound via bindResourceNodes (same ref main.ts mutates). */
  private nodes: ResourceNode[] = [];
  private readonly waveInfo = new Map<
    number,
    { initial: number; kind: "raid" | "assault" }
  >();
  private waveCounter = 0;
  private wavesLaunched = 0;
  private assaultsLaunched = 0;
  private nextWaveAt: number;
  private sweepIdx = 0;
  private unitCounter = 0;
  private simNow = 0;
  /** Last-tick census, for diagnostics. */
  private lastWarbands = 0;
  private lastBuildings = 0;
  /** Tier id chooseMusterTier picked for the next formation (null = wait). */
  private musterChoice: string | null = null;
  /** Last-tick own forces/buildings (for command debug state). */
  private lastOwnFighters: Combatant[] = [];
  private lastOwnActive: PlacedBuilding[] = [];
  /**
   * Road network for the canonical haul_speed path bonus (set by main.ts;
   * null in unit tests that don't build one).
   */
  roads: RoadNetwork | null = null;
  /**
   * Command governor state: true when the director's formations + queued
   * military orders reach its command capacity. tickProduction stops
   * queuing warbands; tickBuildPlanner expands command instead.
   */
  private commandCapped = false;
  /** Per-tick group index: aggro/discovery query groups, never pairwise. */
  private readonly fighterIndex = new SpatialHash<Combatant>(FIGHTER_CELL);
  /** Stagger timers (sim time) for the heavier sub-managers. */
  private nextDefenseAt = 0;
  private nextScoutAt = 0;
  private nextWaveCheckAt = 0;
  private nextPlanAt = 0;
  /** Sim time of the last org-drill start (per-difficulty cadence). */
  private lastOrgStartAt = -1e9;

  constructor(
    private readonly db: GameDB,
    private readonly faction: FactionDef,
    private readonly campX: number,
    private readonly campZ: number,
    readonly difficulty: DifficultyId,
    private readonly cb: AiCallbacks,
    /** Optional PCG terrain: build spots reject water and steep slopes. */
    private readonly terrain?: Heightfield,
  ) {
    this.config = DIFFICULTIES[difficulty];
    const diffIdx = Math.max(0, DIFFICULTY_IDS.indexOf(difficulty));
    const seed =
      (Math.abs(Math.round(campX * 131 + campZ * 17)) ^ (diffIdx * 2654435761)) >>>
      0;
    this.rng = makeRng(seed);
    this.economy = new Economy(db, faction.starting_resources ?? {});
    this.fx = new AiTechEffects(this.research, db, this.config);
    this.nextWaveAt = this.config.firstWaveAt;
    this.commandIds = new Set(
      [...db.factions.values()].flatMap((f) =>
        Object.keys(f.starting_buildings ?? {}),
      ),
    );
    this.commandBuildingId =
      Object.keys(faction.starting_buildings ?? {})[0] ?? "";
    // Data-derived vision bound: max unit vision across every faction's
    // units, with a floor so the query stays sane on sparse data.
    let maxVision = 30;
    for (const u of db.units.values()) {
      maxVision = Math.max(maxVision, num(u.extra["vision"], 0));
    }
    this.maxUnitVision = maxVision;
  }

  // ------------------------------------------------------------------ public

  /**
   * Bind the shared resource-node array (the same reference main.ts
   * mutates when nodes deplete). Must be called before the first tick.
   */
  bindResourceNodes(nodes: ResourceNode[]): void {
    this.nodes = nodes;
  }

  /** Difficulty construction-speed multiplier (main.ts construction tick). */
  get buildSpeedMult(): number {
    return this.config.buildSpeedMult;
  }

  get guardPosts(): number {
    return this.config.guardPosts;
  }

  /** Spawn the faction's starting workers and put them to work. */
  onStart(): void {
    const counts = this.faction.starting_units ?? {};
    let n = 0;
    for (const [unitId, count] of Object.entries(counts)) {
      const unit = this.db.units.get(unitId);
      if (!unit || roleOf(unit) !== "worker") continue;
      for (let i = 0; i < count; i++) {
        n++;
        const w = this.cb.spawnWorker(
          unit,
          this.campX + 8 + n * 2.2,
          this.campZ + 8 - n * 1.4,
        );
        // First assignment: shared nodes are bound already; buildings
        // arrive on the first tick's context.
        this.workforce.autoAssign(w, this.workforceCtx([]));
      }
    }
  }

  tick(dt: number, simNow: number, ctx: AiTickContext): void {
    this.simNow = simNow;
    const ownFighters = ctx.fighters.filter((f) => f.team === 1 && f.alive > 0);
    const ownBuildings = ctx.buildings.filter((b) => b.team === 1);
    const ownActive = ownBuildings.filter((b) => b.state === "active");
    this.lastWarbands = ownFighters.filter((f) =>
      MILITARY_ROLES.has(roleOf(f.unit)),
    ).length;
    this.lastBuildings = ownBuildings.length;
    this.lastOwnFighters = ownFighters;
    this.lastOwnActive = ownActive;

    // Group index: one insert per formation; all acquisition queries below
    // read it instead of scanning the fighter list pairwise.
    this.fighterIndex.clear();
    for (const f of ctx.fighters) {
      if (f.alive <= 0) continue;
      this.fighterIndex.insert(f.view.anchor.x, f.view.anchor.z, f);
    }

    this.tickEconomy(dt, ownActive, ownFighters); // (a) every tick: cheap
    this.workforce.tick(dt, this.workforceCtx(ownActive, ownFighters)); // (b) every tick: cheap
    this.tickResearch(dt, ownActive); // (c) every tick: progress must stay exact (org drill rides here)
    this.tickProduction(dt, ownActive, ownFighters); // (d) every tick: queue progress exact
    if (simNow >= this.nextPlanAt) {
      // (e) staggered: the spiral placement scan is the expensive part
      this.nextPlanAt = simNow + PLAN_EVERY;
      this.tickBuildPlanner(ctx.buildings, ownBuildings);
    }
    if (simNow >= this.nextDefenseAt) {
      // (f) staggered: acquisition runs on the group index
      this.nextDefenseAt = simNow + DEFENSE_EVERY;
      this.tickDefense(ownFighters);
    }
    if (simNow >= this.nextScoutAt) {
      // (g) staggered: waypoint + discovery sweep
      this.nextScoutAt = simNow + SCOUT_EVERY;
      this.tickScouting(ownFighters, ctx.playerTargets, simNow);
    }
    if (simNow >= this.nextWaveCheckAt) {
      // (h) staggered: wave bookkeeping + launch checks
      this.nextWaveCheckAt = simNow + WAVE_EVERY;
      this.tickWaves(ownFighters, ctx.playerTargets, simNow);
    }
  }

  /** Where the AI wants an idle fighter to stand. */
  guardPost(index: number): { x: number; z: number } {
    const angle = (index / this.config.guardPosts) * Math.PI * 2;
    return {
      x: this.campX + Math.cos(angle) * 18,
      z: this.campZ + Math.sin(angle) * 18,
    };
  }

  /** Scouts and wave members hold director orders; main.ts must not drift them. */
  isCommitted(c: Combatant): boolean {
    return this.scouts.has(c) || this.waveMembers.has(c);
  }

  /**
   * Future hook: the resource nodes worth contesting. Today this is the
   * shared node list; a future pass will mark a subset as strategic
   * key-resource points and teach scouting/waves to value them.
   */
  contestableNodes(): ResourceNode[] {
    return this.nodes;
  }

  /**
   * Ordered build plan, derived data-driven from the faction's
   * implemented Echelon I buildings. Public for inspection/debugging.
   */
  buildPlan(): PlanItem[] {
    if (this.plan) return this.plan;
    const idx = Math.max(0, DIFFICULTY_IDS.indexOf(this.difficulty));
    const defs = [...this.db.buildings.values()].filter((b) => {
      if (b.faction !== this.faction.id) return false;
      if (b.extra["implemented"] !== true) return false;
      if (num(b.extra["echelon"], 1) > 1) return false;
      // The planner cannot unlock tech-gated buildings (it researches for
      // economy, not prerequisites), so they never enter the plan.
      const req = b.extra["requires"] as { techs?: string[] } | undefined;
      if ((req?.techs ?? []).length > 0) return false;
      return true;
    });
    const byCost = (a: BuildingDef, b: BuildingDef): number =>
      costSum(costOf(a)) - costSum(costOf(b)) || (a.id < b.id ? -1 : 1);
    const trains = (b: BuildingDef): string[] =>
      (b.extra["trains"] as string[]) ?? [];
    const hasGen = (b: BuildingDef): boolean =>
      typeof (b.extra["generates_resource"] as { kind?: string } | undefined)
        ?.kind === "string";
    const isMilitaryHall = (b: BuildingDef): boolean =>
      trains(b).some((id) => {
        const u = this.db.units.get(id);
        return !!u && MILITARY_ROLES.has(roleOf(u));
      });
    const isDefensive = (b: BuildingDef): boolean =>
      this.config.defensiveBuildings.some((s) => b.id.includes(s));
    const isEconHub = (b: BuildingDef): boolean => {
      if (hasGen(b) || trains(b).length > 0 || isDefensive(b)) return false;
      const researched = (b.extra["researches"] as string[]) ?? [];
      return researched.some((t) => /yield|haul|load/.test(t));
    };
    const field = defs.filter(hasGen).sort(byCost)[0];
    const halls = defs.filter(isMilitaryHall).sort(byCost);
    const hubs = defs.filter(isEconHub).sort(byCost);
    const defensive = defs.filter(isDefensive).sort(byCost);
    const items: PlanItem[] = [];
    if (field) items.push({ def: field, want: 2 });
    for (const h of hubs.slice(0, 3)) items.push({ def: h, want: 1 });
    if (halls[0]) items.push({ def: halls[0], want: 1 });
    const defWant = [1, 2, 3][idx] ?? 1;
    defensive.forEach((d, i) => {
      if (i < defWant) items.push({ def: d, want: 1 });
    });
    if (field) items.push({ def: field, want: [3, 4, 5][idx] ?? 3 });
    this.plan = items;
    return items;
  }

  debugState(): AiDebugState {
    const cmd = commandStatus(
      this.lastOwnFighters,
      1,
      this.queues,
      this.db,
      this.lastOwnActive,
      this.fx,
    );
    return {
      difficulty: this.difficulty,
      simNow: this.simNow,
      playerBaseKnown: this.knowledge.playerBase !== null,
      scoutsActive: this.scouts.size,
      wavesLaunched: this.wavesLaunched,
      assaultsLaunched: this.assaultsLaunched,
      warbands: this.lastWarbands,
      workers: this.workforce.workers.length,
      aiStocks: this.economy.stockRecord(),
      buildingsOwned: this.lastBuildings,
      researchCompleted: [...this.research.completed],
      musterTier: this.aiTier().id,
      /** PROTOTYPE: tier id the muster governor chose this tick (null = hold). */
      musterChoice: this.musterChoice,
      commandUsed: cmd.used,
      commandTotal: cmd.total,
    };
  }

  // ------------------------------------------------------------- sub-managers

  /** (a) Economy: income from own buildings, upkeep from own forces. */
  private tickEconomy(
    dt: number,
    ownActive: PlacedBuilding[],
    ownFighters: Combatant[],
  ): void {
    const prodMult: Record<string, number> = {};
    const flatIncome: Record<string, number> = {};
    for (const id of this.db.resources.keys()) {
      prodMult[id] = this.fx.gatherMultiplier(id);
      const add = this.fx.incomeAdd(id);
      if (add !== 0) flatIncome[id] = add;
    }
    // Formation upkeep: assessed per formation on paper strength
    // (sim/organization.ts). The difficulty logistics handicap scales what
    // the AI actually pays; morale/debt/desertion apply to AI formations
    // exactly like the player's.
    tickFormationUpkeep(
      dt,
      ownFighters,
      1,
      (amount) => {
        const s = this.economy.stocks.get("food") ?? 0;
        const paid = Math.min(s, amount);
        if (paid > 0) this.economy.add("food", -paid);
        return paid;
      },
      this.fx.upkeepMultiplier(),
      this.config.upkeepMult,
    );
    // Difficulty logistics handicap on the remaining (civilian) upkeep.
    const upkeep = workforceUpkeep(this.workforce.workers);
    for (const k of Object.keys(upkeep)) upkeep[k] *= this.config.upkeepMult;
    this.economy.tick(
      dt,
      ownActive,
      upkeep,
      prodMult,
      this.fx.upkeepMultiplier(),
      flatIncome,
    );
  }

  /**
   * Food the AI's farms currently produce per second (hands actually
   * working, difficulty gather rate applied). The military governor uses
   * this so the AI never trains itself into a death spiral.
   */
  private foodIncomePerSec(ownActive: PlacedBuilding[]): number {
    let income = 0;
    for (const b of ownActive) {
      const res = farmResource(b);
      if (!res || res.kind !== "food") continue;
      income +=
        (res.amount / res.interval) *
        farmHands(b, this.workforce.workers) *
        this.config.gatherRateMult;
    }
    return income;
  }

  /** Total food/sec the standing army burns, after difficulty + tech multipliers. */
  private standingBurnPerSec(ownFighters: Combatant[]): number {
    let n = 0;
    for (const f of ownFighters) {
      if (f.team !== 1 || f.alive <= 0) continue;
      if (!MILITARY_ROLES.has(roleOf(f.unit))) continue;
      n += formationUpkeepPerSec(f.paperStrength ?? 0);
    }
    return n * this.config.upkeepMult * this.fx.upkeepMultiplier();
  }

  /** Food/sec one formation of this tier burns, after difficulty + tech multipliers. */
  private tierUpkeepPerSec(tier: OrgTier): number {
    return (
      formationUpkeepPerSec(tier.paperStrength) *
      this.config.upkeepMult *
      this.fx.upkeepMultiplier()
    );
  }

  /**
   * Muster tier choice: the largest unlocked tier whose projected upkeep
   * fits inside measured farm income. The projection sums the standing
   * army's burn PLUS every already-queued military order's upkeep (by
   * locked tierId) PLUS the candidate tier — a queued expensive
   * formation must not let a second slot reuse the same headroom.
   * The AI drills up through ORG_TECH_LINE (maybeDrillOrg) and musters
   * at the best tier its logistics sustain — larger formations for the
   * same command, without bankrupting itself. Returns null when nothing
   * affordable is unlocked: the AI waits instead of training into a
   * death spiral. A seed force (PROTOTYPE_SEED_FORCE) may muster at the
   * cheapest unlocked tier from the starting stockpile before farms exist.
   */
  private chooseMusterTier(
    ownFighters: Combatant[],
    ownActive: PlacedBuilding[],
  ): OrgTier | null {
    const unlocked = tiersForFaction(this.faction.id).filter(
      (t) => !t.techGate || this.research.completed.has(t.techGate),
    );
    if (unlocked.length === 0) return null;
    // unlocked is sorted by paper strength ascending: [0] is the baseline.
    if (this.lastWarbands < PROTOTYPE_SEED_FORCE) return unlocked[0];
    const income = this.foodIncomePerSec(ownActive);
    const committed =
      this.standingBurnPerSec(ownFighters) + this.queuedMilitaryBurnPerSec();
    const headroom = Math.max(income, 0) * PROTOTYPE_FOOD_HEADROOM;
    for (let i = unlocked.length - 1; i >= 0; i--) {
      const t = unlocked[i];
      if (committed + this.tierUpkeepPerSec(t) <= headroom) return t;
    }
    return null;
  }

  private workforceCtx(
    buildings: PlacedBuilding[],
    ownFighters: Combatant[] = [],
  ): WorkforceContext {
    const own = buildings.filter((b) => b.team === 1 && b.state === "active");
    const farms = own.filter((b) => farmResource(b) !== null);
    // Staff the farms first while hands are short of what the food
    // economy needs: enough to cover the standing army's burn plus a
    // margin to stockpile training costs. Surplus workers keep the
    // default nodes-first behavior for timber/stone.
    let ratePerHand = 0;
    let farmCount = 0;
    for (const b of farms) {
      const res = farmResource(b);
      if (!res || res.kind !== "food") continue;
      ratePerHand += (res.amount / res.interval) * this.config.gatherRateMult;
      farmCount++;
    }
    let farmsFirst = false;
    if (farmCount > 0 && ratePerHand > 0) {
      ratePerHand /= farmCount;
      const burnPerSec =
        ownFighters
          .filter(
            (f) =>
              f.team === 1 &&
              f.alive > 0 &&
              MILITARY_ROLES.has(roleOf(f.unit)),
          )
          .reduce((n, f) => n + formationUpkeepPerSec(f.paperStrength), 0) *
        this.config.upkeepMult *
        this.fx.upkeepMultiplier();
      const desired = Math.ceil((burnPerSec * 1.5 + 0.3) / ratePerHand);
      const hands = farms.reduce(
        (n, f) => n + farmHands(f, this.workforce.workers),
        0,
      );
      farmsFirst = hands < desired;
    }
    return {
      nodes: this.nodes,
      farms,
      forums: own.filter((b) => b.def.id === this.commandBuildingId),
      sites: buildings,
      roads: this.roads,
      economy: this.economy,
      fx: this.fx,
      farmsFirst,
    };
  }

  /** (c) Research: org drill first when due, else best affordable echelon-1 tech. */
  private tickResearch(dt: number, ownActive: PlacedBuilding[]): void {
    if (!this.research.active && !this.maybeDrillOrg()) {
      const ids = new Set(ownActive.map((b) => b.def.id));
      const denariiReserve = this.drillDenariiReserve();
      // PROTOTYPE: while saving for a due org drill (food is the missing
      // piece), economy research must not spend the drill's food
      // stockpile either — the same growth-spending trap that holds
      // military training, mirrored here for the research line.
      const savingForDrill = this.drillPending();
      const pendingDrill = savingForDrill ? this.pendingDrillTech() : null;
      const drillFoodReserve = pendingDrill
        ? ((pendingDrill.cost["food"] as number) ?? 0) + this.config.orgReserveFood
        : 0;
      let best: TechDef | null = null;
      let bestScore = -1;
      let bestCost = Infinity;
      let bestId = "";
      for (const t of this.db.technologies.values()) {
        if (!canResearch(t, this.research, ids, 1).ok) continue;
        // PROTOTYPE: budget for the org drill — denarii is not renewable
        // for every faction (the Samnite meeting house has no denarii
        // income in data), so economy research must not spend the
        // drill's denarii before the cadence comes due. Food/iron/timber
        // are renewable and handled by the drill's own reserve gate.
        const denariiCost = t.cost["denarii"] ?? 0;
        if (
          denariiCost > 0 &&
          (this.economy.stocks.get("denarii") ?? 0) - denariiCost <
            denariiReserve
        ) {
          continue;
        }
        const foodCost = t.cost["food"] ?? 0;
        if (
          savingForDrill &&
          foodCost > 0 &&
          (this.economy.stocks.get("food") ?? 0) - foodCost < drillFoodReserve
        ) {
          continue;
        }
        const s = scoreResearch(t);
        const c = costSum(t.cost);
        if (
          s > bestScore ||
          (s === bestScore &&
            (c < bestCost || (c === bestCost && t.id < bestId)))
        ) {
          best = t;
          bestScore = s;
          bestCost = c;
          bestId = t.id;
        }
      }
      if (best && this.economy.canAfford(best.cost)) {
        startResearch(this.research, best, this.economy);
      }
    }
    tickResearch(
      this.research,
      dt * this.config.researchSpeedMult,
      this.db,
    );
  }

  /**
   * PROTOTYPE: organization drill. The AI "drills in the field" — it
   * abstracts the canonical building/echelon/research-site prerequisites
   * of its org techs (E2+ drill grounds the prototype AI never
   * constructs) and follows ORG_TECH_LINE in order on a per-difficulty
   * time cadence (DifficultyConfig.org*), paying the full research cost
   * from surplus. Drilling up raises the muster tier for all future
   * formations (drills without the age-up): bigger formations for the
   * same command, in exchange for proportionally larger training
   * cost/time and sublinearly-scaled upkeep. Takes priority over the
   * economy research line when a drill is due, so the cadence cannot
   * starve behind an endless economy queue.
   *
   * Returns true when an org drill started (caller skips the economy scan).
   */
  /**
   * PROTOTYPE: the next org-drill tech within the difficulty's org
   * depth, or null when none remains (depth exhausted or a data
   * anomaly). Shared by the drill starter, the denarii/food budgeting,
   * and the "saving for the drill" training pause below. When
   * respectCadence is false the cadence gates are skipped: the denarii
   * reserve uses that form so economy research holds the drill's
   * denarii back from the start (denarii is not renewable for every
   * faction), not just when the cadence comes due.
   */
  private nextDrillTech(respectCadence: boolean): TechDef | null {
    const cfg = this.config;
    if (respectCadence) {
      if (this.simNow < cfg.orgStartAt) return null;
      if (this.simNow - this.lastOrgStartAt < cfg.orgInterval) return null;
    }
    const line = ORG_TECH_LINE[this.faction.id] ?? [];
    let depth = 0;
    for (const techId of line) {
      if (this.research.completed.has(techId)) {
        depth++;
        continue;
      }
      if (depth >= cfg.orgMaxTechs) return null;
      return this.db.technologies.get(techId) ?? null; // never invent techs
    }
    return null;
  }

  /** Next org-drill tech due on the difficulty cadence, or null. */
  private pendingDrillTech(): TechDef | null {
    return this.nextDrillTech(true);
  }

  /**
   * PROTOTYPE: true when an org drill is due and unstarted. While saving,
   * the AI pauses new military training and food-cost economy research
   * so the drill's stockpile can actually form — growth spending
   * otherwise holds food under the drill cost forever. The posture
   * holds until the drill STARTS (not merely until the stockpile
   * momentarily covers the cost): if the research slot is busy when
   * food first reaches the cost, unpausing would let training eat the
   * stockpile before the slot frees and the drill would starve.
   * Returns false unless every non-food cost is payable now — if the
   * drill is blocked on something else it isn't imminent and training
   * must not stall for it. Worker training is NOT paused: workers are
   * the economy that funds the drill.
   */
  private drillPending(): boolean {
    const tech = this.pendingDrillTech();
    if (!tech) return false;
    for (const [res, amt] of Object.entries(tech.cost)) {
      if (res === "food") continue;
      if ((this.economy.stocks.get(res) ?? 0) < (amt as number)) return false;
    }
    return true;
  }

  private maybeDrillOrg(): boolean {
    const cfg = this.config;
    const tech = this.pendingDrillTech();
    if (!tech) return false;
    if ((this.economy.stocks.get("food") ?? 0) <= cfg.orgReserveFood) {
      return false;
    }
    if (!this.economy.canAfford(tech.cost)) return false;
    if (startResearch(this.research, tech, this.economy)) {
      this.lastOrgStartAt = this.simNow;
      this.cb.notify(
        `Enemy drills upward: <b>${tech.name_en}</b> — larger formations incoming.`,
      );
      return true;
    }
    return false;
  }

  /**
   * PROTOTYPE: denarii the AI holds back for its next org drill. Returns
   * the pending drill tech's denarii cost, or 0 when no drill is due.
   * Denarii is always reserved once a drill is due because it is not
   * renewable for every faction (the Samnite meeting house has no
   * denarii income in data). Food is handled separately: the drill's
   * own reserve gate at start time, plus a saving pause (drillPending)
   * that keeps growth spending from eating the drill's stockpile.
   */
  private drillDenariiReserve(): number {
    const tech = this.nextDrillTech(false);
    return (tech?.cost["denarii"] as number) ?? 0;
  }

  /** AI's current muster tier: best unlocked by its org research (drills w/o age-up). */
  private aiTier(): OrgTier {
    return bestTierForFaction(this.faction.id, this.research.completed);
  }

  /** (d) Military production: workers to target, warbands to cap. */
  private tickProduction(
    dt: number,
    ownActive: PlacedBuilding[],
    ownFighters: Combatant[],
  ): void {
    const workerDef = this.workerDef();
    const queuedWorkers = workerDef ? this.queuedRoleCount("worker") : 0;
    const needWorkers =
      workerDef !== null &&
      this.workforce.workers.length + queuedWorkers < this.config.workerTarget;
    const militaryCount = this.lastWarbands + this.queuedMilitaryCount();
    // Projected food governor: growth is sized on the NEXT tier's upkeep —
    // the tier the AI is about to muster — never on the current average.
    // The muster tier itself is the largest unlocked tier whose upkeep
    // fits inside measured farm income (chooseMusterTier), and the commit
    // check below projects standing burn + that tier's upkeep before
    // training, so drilling up can never silently bankrupt the army.
    // Never shrinks the standing force (no disbanding).
    const tier = this.chooseMusterTier(ownFighters, ownActive);
    this.musterChoice = tier ? tier.id : null;
    const nextUpkeepPerSec = tier ? this.tierUpkeepPerSec(tier) : 0;
    const income = this.foodIncomePerSec(ownActive);
    const standingBurn = this.standingBurnPerSec(ownFighters);
    const queuedBurn = this.queuedMilitaryBurnPerSec();
    const affordable =
      tier && nextUpkeepPerSec > 0 && income > 0
        ? Math.floor(income / nextUpkeepPerSec)
        : 0;
    const wantWarbands = Math.min(
      this.config.maxWarbands,
      Math.max(this.lastWarbands, PROTOTYPE_SEED_FORCE, affordable),
    );
    // Commit-time projection: the queued formation's upkeep must fit
    // inside farm income alongside everything standing AND everything
    // already queued (by locked tierId). The seed force bypasses this
    // (it musters from the starting stockpile).
    const projectedFits =
      this.lastWarbands < PROTOTYPE_SEED_FORCE ||
      standingBurn + queuedBurn + nextUpkeepPerSec <=
        Math.max(income, 0) * PROTOTYPE_FOOD_HEADROOM;
    // Command governor: never train past the formation cap. Each faction
    // has its own pool (own buildings only); workers cost 0. When capped,
    // the build planner expands command instead of stalling.
    const cmd = commandStatus(
      ownFighters,
      1,
      this.queues,
      this.db,
      ownActive,
      this.fx,
    );
    this.commandCapped = cmd.used >= cmd.total;
    const needMilitary =
      !this.commandCapped &&
      tier !== null &&
      militaryCount < wantWarbands &&
      projectedFits &&
      !this.drillPending() && // saving for the org drill outranks growth
      (this.economy.stocks.get("food") ?? 0) > 50;

    if (needWorkers && workerDef) {
      const b = ownActive.find((x) =>
        trainableUnits(x, this.db, 1, this.research).some(
          (u) => u.id === workerDef.id,
        ),
      );
      if (
        b &&
        this.queues.queueOf(b.uid).length < 2 &&
        this.economy.pay(unitCost(workerDef))
      ) {
        this.queues.enqueue(b.uid, workerDef);
      }
    }
    if (
      needMilitary &&
      tier &&
      (!needWorkers || this.rng() < this.config.militarySpendBias)
    ) {
      const pick = this.chooseMilitaryUnit(ownActive);
      // Muster tier was chosen by the projected governor above and is
      // locked at enqueue (what the AI pays for is what musters); cost
      // and time scale with the tier's paper strength.
      const scaled =
        pick && this.queues.queueOf(pick.building.uid).length < 2
          ? tierTrainCost(unitCost(pick.unit), tier)
          : null;
      if (pick && scaled) {
        if (this.economy.pay(scaled)) {
          this.queues.enqueue(
            pick.building.uid,
            pick.unit,
            tier.id,
            trainScale(tier),
          );
        }
      }
    }

    for (const done of this.queues.tick(dt * this.config.trainSpeedMult)) {
      this.completeTraining(done.buildingUid, done.unitId, ownActive, done.tierId);
    }
  }

  private workerDef(): UnitDef | null {
    let best: UnitDef | null = null;
    let bestCost = Infinity;
    for (const u of this.db.units.values()) {
      if (
        u.faction !== this.faction.id ||
        roleOf(u) !== "worker" ||
        u.echelon > 1 ||
        u.extra["implemented"] === false
      ) {
        continue;
      }
      const c = costSum(unitCost(u));
      if (c < bestCost) {
        best = u;
        bestCost = c;
      }
    }
    return best;
  }

  private queuedRoleCount(role: string): number {
    let n = 0;
    for (const o of this.queues.allOrders()) {
      const u = this.db.units.get(o.unitId);
      if (u && roleOf(u) === role) n++;
    }
    return n;
  }

  private queuedMilitaryCount(): number {
    return queuedMilitaryCount(this.queues, this.db);
  }

  /**
   * PROTOTYPE: total food/sec the already-queued military orders will
   * burn once they muster, summed by each order's locked tierId (what
   * the AI pays for is what musters). Orders with an unknown tierId are
   * skipped rather than guessed — the projection must stay honest.
   */
  private queuedMilitaryBurnPerSec(): number {
    let burn = 0;
    for (const o of this.queues.allOrders()) {
      const u = this.db.units.get(o.unitId);
      if (!u || !MILITARY_ROLES.has(roleOf(u))) continue;
      const tier = tierById(o.tierId);
      if (!tier) continue;
      burn += this.tierUpkeepPerSec(tier);
    }
    return burn;
  }

  /** Cheapest trainable unit of a comp-weighted role, at an owned building. */
  private chooseMilitaryUnit(
    ownActive: PlacedBuilding[],
  ): { unit: UnitDef; building: PlacedBuilding } | null {
    const w = this.config.compWeights;
    const total = w.infantry + w.skirmisher + w.cavalry || 1;
    const r = this.rng() * total;
    const first =
      r < w.infantry ? "infantry" : r < w.infantry + w.skirmisher ? "skirmisher" : "cavalry";
    const roles = [first, "infantry", "skirmisher", "cavalry"].filter(
      (x, i, a) => a.indexOf(x) === i,
    );
    for (const role of roles) {
      const candidates = [...this.db.units.values()]
        .filter(
          (u) =>
            u.faction === this.faction.id &&
            roleOf(u) === role &&
            u.echelon <= 1 &&
            u.extra["implemented"] !== false,
        )
        .sort(
          (a, b) =>
            costSum(unitCost(a)) - costSum(unitCost(b)) ||
            (a.id < b.id ? -1 : 1),
        );
      for (const unit of candidates) {
        const b = ownActive.find((x) =>
          trainableUnits(x, this.db, 1, this.research).some(
            (u) => u.id === unit.id,
          ),
        );
        if (b) return { unit, building: b };
      }
    }
    return null;
  }

  private completeTraining(
    buildingUid: number,
    unitId: string,
    ownActive: PlacedBuilding[],
    tierId: string,
  ): void {
    const b = ownActive.find((x) => x.uid === buildingUid);
    const unit = this.db.units.get(unitId);
    if (!b || !unit) return;
    if (roleOf(unit) === "worker") {
      const w = this.cb.spawnWorker(unit, b.x + 6, b.z + 6);
      this.workforce.autoAssign(w, this.workforceCtx(ownActive));
    } else {
      this.unitCounter++;
      // Muster tier was locked at enqueue; fall back to the current best
      // tier for orders queued before organization landed.
      const tier = tierById(tierId) ?? this.aiTier();
      this.cb.spawnMilitary(
        unit,
        b.x + 12,
        b.z + 4,
        `${tier.name_en} ${this.unitCounter}`,
        tier,
      );
    }
  }

  /** (e) Build planner: walk the plan, one construction at a time (per difficulty). */
  private tickBuildPlanner(
    allBuildings: PlacedBuilding[],
    ownBuildings: PlacedBuilding[],
  ): void {
    const maxConcurrent = this.difficulty === "expert" ? 2 : 1;
    const constructing = ownBuildings.filter(
      (b) => b.state === "constructing",
    ).length;
    if (constructing >= maxConcurrent) return;
    const ownActive = ownBuildings.filter((b) => b.state === "active");
    // Command expansion first: capped on formations and still wanting
    // warbands → build another command-providing military hall instead of
    // stalling at the cap forever. The food governor still paces training.
    if (this.commandCapped && this.tryCommandExpansion(allBuildings, ownBuildings, ownActive)) {
      return;
    }
    for (const item of this.buildPlan()) {
      const owned = ownBuildings.filter((b) => b.def.id === item.def.id).length;
      if (owned >= item.want) continue;
      if (!this.buildable(item.def, ownActive)) continue;
      if (!this.economy.canAfford(costOf(item.def))) continue;
      const spot = this.findBuildSpot(item.def, allBuildings);
      if (!spot) continue;
      if (!this.economy.pay(costOf(item.def))) continue;
      this.cb.placeBuilding(item.def, spot.x, spot.z, false, 1);
      return;
    }
  }

  /** Echelon 1, implemented, prerequisites satisfiable (no tech-gating). */
  private buildable(def: BuildingDef, ownActive: PlacedBuilding[]): boolean {
    if (def.extra["implemented"] !== true) return false;
    if (num(def.extra["echelon"], 1) > 1) return false;
    if (missingRequirements(def, ownActive).length > 0) return false;
    const req = def.extra["requires"] as { techs?: string[] } | undefined;
    if (req?.techs?.some((t) => !this.research.completed.has(t))) return false;
    return true;
  }

  /**
   * Cheapest buildable military hall that provides command — the
   * expansion target when the director hits its formation cap.
   */
  private commandHallDef(ownActive: PlacedBuilding[]): BuildingDef | null {
    const halls = [...this.db.buildings.values()]
      .filter((b) => b.faction === this.faction.id)
      .filter((b) => commandProvided(b) > 0)
      .filter((b) =>
        ((b.extra["trains"] as string[]) ?? []).some((id) => {
          const u = this.db.units.get(id);
          return !!u && MILITARY_ROLES.has(roleOf(u));
        }),
      )
      .filter((b) => this.buildable(b, ownActive))
      .sort(
        (a, b) =>
          costSum(costOf(a)) - costSum(costOf(b)) || (a.id < b.id ? -1 : 1),
      );
    return halls[0] ?? null;
  }

  /**
   * Place one additional command-providing military hall. Guarded: never
   * while one is already under construction (the cap only rises on
   * completion), so the planner converges instead of spamming buildings.
   */
  private tryCommandExpansion(
    allBuildings: PlacedBuilding[],
    ownBuildings: PlacedBuilding[],
    ownActive: PlacedBuilding[],
  ): boolean {
    const expanding = ownBuildings.some(
      (b) => b.state === "constructing" && commandProvided(b.def) > 0,
    );
    if (expanding) return false;
    const hall = this.commandHallDef(ownActive);
    if (!hall) return false;
    if (!this.economy.canAfford(costOf(hall))) return false;
    const spot = this.findBuildSpot(hall, allBuildings);
    if (!spot) return false;
    if (!this.economy.pay(costOf(hall))) return false;
    this.cb.placeBuilding(hall, spot.x, spot.z, false, 1);
    return true;
  }

  /** Deterministic spiral search for a free camp-relative building spot. */
  private findBuildSpot(
    def: BuildingDef,
    allBuildings: PlacedBuilding[],
  ): { x: number; z: number } | null {
    const [w, d] = footprintOf(def);
    const step = Math.max(w, d) + 3;
    for (let ring = 1; ring <= 12; ring++) {
      const r = ring * step;
      const steps = Math.max(8, ring * 6);
      for (let i = 0; i < steps; i++) {
        const a = (i / steps) * Math.PI * 2 + ring * 0.7;
        const x = this.campX + Math.cos(a) * r;
        const z = this.campZ + Math.sin(a) * r;
        if (Math.abs(x) > 190 || Math.abs(z) > 190) continue;
        // Reject water and cliffs when terrain is available.
        if (this.terrain) {
          if (this.terrain.isWaterAt(x, z)) continue;
          if (this.terrain.slopeAt(x, z) > 0.55) continue;
        }
        if (!overlaps(def, x, z, allBuildings)) return { x, z };
      }
    }
    return null;
  }

  /**
   * (f) Defense: idle warbands guard, chase intruders, leash home.
   * Group-level: ONE spatial query finds intruders near the camp; each
   * warband then picks the nearest from that small set. Never a
   * warbands×fighters pairwise scan.
   */
  private tickDefense(ownFighters: Combatant[]): void {
    const intruders: Combatant[] = [];
    this.fighterIndex.forEachInRadius(
      this.campX,
      this.campZ,
      this.config.aggroRange,
      (f) => {
        if (f.team === 0 && f.alive > 0) intruders.push(f);
      },
    );
    for (const c of ownFighters) {
      if (c.routing || this.isCommitted(c)) continue;
      const dCamp = dist2D(c.view.anchor, { x: this.campX, z: this.campZ });
      const target = c.order?.target;
      const alive =
        target &&
        (target.kind === "building" ? target.building.hp > 0 : target.alive > 0);
      if (target && alive) {
        if (target.kind !== "building" && dCamp > this.config.leashRange) {
          c.order = null; // give up the chase, drift home via guard posts
        }
        continue;
      }
      // (Re)acquire: nearest intruder to the camp, from the index result.
      let nearest: Combatant | null = null;
      let nearestD = Infinity;
      for (const f of intruders) {
        const d = dist2D(f.view.anchor, { x: this.campX, z: this.campZ });
        if (d < nearestD) {
          nearest = f;
          nearestD = d;
        }
      }
      c.order = nearest ? { target: nearest } : null;
    }
  }

  /** (g) Scouting: sweep the map until something sees the player base. */
  private tickScouting(
    ownFighters: Combatant[],
    playerTargets: BuildingTarget[],
    simNow: number,
  ): void {
    for (const c of [...this.scouts.keys()]) {
      if (c.alive <= 0 || !ownFighters.includes(c)) this.scouts.delete(c);
    }
    // Scout want: leave at least one warband home for defense. Before
    // discovery the full scout complement sweeps; once the base is known
    // a single scout keeps intel fresh and the rest join the wave pool.
    const military = ownFighters.filter(
      (c) => MILITARY_ROLES.has(roleOf(c.unit)) && !c.routing,
    ).length;
    const maxScoutable = Math.max(0, military - 1);
    const want = this.knowledge.playerBase
      ? Math.min(1, maxScoutable)
      : Math.min(this.config.maxScouts, maxScoutable);
    while (this.scouts.size > want) {
      const next = this.scouts.keys().next();
      if (next.done) break;
      this.scouts.delete(next.value);
    }
    const candidates = ownFighters
      .filter(
        (c) =>
          !this.isCommitted(c) &&
          // Warbands holding an attack order stay on defense; drafting
          // them as scouts would strand the order outside the leash logic.
          c.order == null &&
          MILITARY_ROLES.has(roleOf(c.unit)) &&
          !c.routing,
      )
      .sort(
        (a, b) =>
          scoutRank(a.unit) - scoutRank(b.unit) ||
          statNum(b.unit, "move_speed", 4.5) - statNum(a.unit, "move_speed", 4.5),
      );
    while (this.scouts.size < want && candidates.length > 0) {
      const c = candidates.shift()!;
      const wp = this.newWaypoint(simNow);
      this.scouts.set(c, wp);
      this.cb.orderMove(c, wp.x, wp.z);
    }
    for (const [c, wp] of this.scouts) {
      if (
        dist2D(c.view.anchor, wp) < 8 ||
        simNow - wp.at > this.config.scoutInterval
      ) {
        const nwp = this.newWaypoint(simNow);
        this.scouts.set(c, nwp);
        this.cb.orderMove(c, nwp.x, nwp.z);
      }
    }
    // Discovery: a player building is "seen" when an AI group comes
    // within its own data `vision` range. Group-level: one spatial query
    // per player building (buildings are few) instead of a
    // warbands×buildings pairwise scan. Nothing is known until this happens.
    for (const t of playerTargets) {
      if (t.building.hp <= 0) continue;
      let seen = false;
      this.fighterIndex.forEachInRadius(
        t.building.x,
        t.building.z,
        this.maxUnitVision,
        (c) => {
          if (seen || c.team !== 1 || c.alive <= 0 || c.routing) return;
          const vision = num(c.unit.extra["vision"], 30);
          if (dist2D(c.view.anchor, t.building) <= vision) {
            this.knowledge.playerBase = {
              x: t.building.x,
              z: t.building.z,
              seenAt: simNow,
            };
            seen = true;
          }
        },
      );
      if (seen) break;
    }
    // The trail goes cold: no player building near the last sighting.
    const kb = this.knowledge.playerBase;
    if (
      kb &&
      !playerTargets.some((t) => dist2D(t.building, kb) <= 60)
    ) {
      this.knowledge.playerBase = null;
    }
  }

  /** Seeded quadrant sweep, kept clear of the camp itself. */
  private newWaypoint(simNow: number): Waypoint {
    const quads = [
      [1, 1],
      [-1, 1],
      [1, -1],
      [-1, -1],
    ];
    for (let tries = 0; tries < 6; tries++) {
      const q = quads[this.sweepIdx++ % quads.length];
      const jitter = this.config.scoutWaypointJitter;
      const x = Math.max(
        -185,
        Math.min(185, q[0] * 95 + (this.rng() * 2 - 1) * jitter),
      );
      const z = Math.max(
        -185,
        Math.min(185, q[1] * 95 + (this.rng() * 2 - 1) * jitter),
      );
      if (dist2D({ x, z }, { x: this.campX, z: this.campZ }) > 50) {
        return { x, z, at: simNow };
      }
    }
    return { x: 0, z: 0, at: simNow };
  }

  /** (h) Waves: repeatable pressure, gated on knowledge — never one-shot. */
  private tickWaves(
    ownFighters: Combatant[],
    playerTargets: BuildingTarget[],
    simNow: number,
  ): void {
    this.maintainWaves(ownFighters);
    const kb = this.knowledge.playerBase;
    if (!kb || simNow < this.nextWaveAt) return;

    let kind: "raid" | "assault" =
      this.difficulty === "easy"
        ? "raid"
        : this.wavesLaunched % 2 === 0
          ? "raid"
          : "assault";
    if (kind === "assault" && simNow < this.config.allOutAt) kind = "raid";
    const need = kind === "raid" ? this.config.raidSize : this.config.waveSize;

    const pool = ownFighters.filter(
      (c) =>
        !this.isCommitted(c) &&
        MILITARY_ROLES.has(roleOf(c.unit)) &&
        !c.routing,
    );
    // Recall scouts to fill the wave: the base is known, so their
    // intelligence mission is done — they march with the wave and fresh
    // scouts draft afterward from whatever stays home.
    if (pool.length < need) {
      for (const c of [...this.scouts.keys()]) {
        if (pool.length >= need) break;
        if (
          c.alive > 0 &&
          !c.routing &&
          MILITARY_ROLES.has(roleOf(c.unit))
        ) {
          this.scouts.delete(c);
          pool.push(c);
        }
      }
    }
    if (pool.length < need) return;

    const isCommand = (t: BuildingTarget): boolean =>
      this.commandIds.has(t.building.def.id);
    let target: BuildingTarget | null = null;
    if (kind === "assault") target = playerTargets.find(isCommand) ?? null;
    if (!target) {
      let bestD = Infinity;
      for (const t of playerTargets) {
        if (kind === "raid" && isCommand(t)) continue;
        const d = dist2D(t.building, kb);
        if (d < bestD) {
          bestD = d;
          target = t;
        }
      }
      // Turtled player (command building only): a raid still falls on the
      // base itself. Without this fallback no wave can ever launch and the
      // AI sits idle forever — wavesLaunched never advances to assaults.
      if (!target) {
        for (const t of playerTargets) {
          const d = dist2D(t.building, kb);
          if (d < bestD) {
            bestD = d;
            target = t;
          }
        }
      }
    }
    if (!target) return;

    // Expert leads with cavalry; everyone else marches in pool order.
    const sorted = [...pool].sort((a, b) =>
      this.difficulty === "expert"
        ? scoutRank(a.unit) - scoutRank(b.unit)
        : 0,
    );
    const members = sorted.slice(0, need);
    const waveId = ++this.waveCounter;
    for (const c of members) {
      c.order = { target };
      this.waveMembers.set(c, waveId);
    }
    this.waveInfo.set(waveId, { initial: members.length, kind });
    this.wavesLaunched++;
    if (kind === "assault") this.assaultsLaunched++;
    // Always reschedule: pressure repeats on a timer, win or lose.
    this.nextWaveAt = simNow + this.config.waveInterval;
    this.cb.notify(
      kind === "assault"
        ? `The ${this.faction.name_en} assault your ${target.building.def.name_en}!`
        : "Enemy raiders have been spotted.",
    );
  }

  /** Release dead/finished members; break waves that collapse below 40%. */
  private maintainWaves(ownFighters: Combatant[]): void {
    const aliveByWave = new Map<number, number>();
    for (const [c, waveId] of [...this.waveMembers]) {
      if (c.alive <= 0 || !ownFighters.includes(c) || c.order == null) {
        this.waveMembers.delete(c);
        continue;
      }
      aliveByWave.set(waveId, (aliveByWave.get(waveId) ?? 0) + 1);
    }
    for (const [waveId, info] of [...this.waveInfo]) {
      const alive = aliveByWave.get(waveId) ?? 0;
      if (alive === 0) {
        this.waveInfo.delete(waveId);
        continue;
      }
      if (alive < info.initial * 0.4) {
        for (const [c, id] of [...this.waveMembers]) {
          if (id === waveId) {
            c.order = null;
            this.waveMembers.delete(c);
          }
        }
        this.waveInfo.delete(waveId);
      }
    }
  }
}
